import { TgkParameterMetadata } from "../../api/models/tgk-parameter-metadata";
import { CoObjectParameters, DBApplication, DimensionDetails } from "../interfaces/CoObject";
import { TreeDropDownInput } from "../coobjects/tree-pills-input/TreeInput";
import { fetchParametersChildren, fetchParametersRoot, mapParameterToDimensionItem } from "../../api/object/Parameters";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import React, { ReactElement } from "react";
import { TextField } from "@fluentui/react/lib/TextField";
import {
  Checkbox,
  DatePicker,
  defaultDatePickerStrings,
  Dropdown,
  Position,
  ResponsiveMode,
  SpinButton,
} from "@fluentui/react";
import { Label } from "@fluentui/react/lib/Label";
import { ReferringScenarios } from "../../api/models/ReferringScenarios";

const getLabelRendererWithCloseIcon = (onClose: () => void, props: any, defaultRender: (any) => ReactElement) => {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      {defaultRender(props)}
      <span className="k-icon k-i-close" style={{ alignSelf: "flex-end", marginBottom: "6px" }} onClick={onClose} />
    </div>
  );
};

export const ParameterInput = ({
  metadata,
  isLocked,
  objectParameters,
  updateParameters,
  dbApplication,
  formId,
  inheritableParameters,
  isComponentInstance,
  referringScenarios,
}: {
  metadata: TgkParameterMetadata;
  objectParameters: CoObjectParameters;
  updateParameters: (newParameters: CoObjectParameters) => void;
  dbApplication: DBApplication;
  formId: string;
  inheritableParameters: DimensionDetails[] | undefined;
  isComponentInstance: boolean;
  isLocked: boolean;
  referringScenarios?: ReferringScenarios;
}) => {
  const updateParameterFromValue = (metadata: TgkParameterMetadata, newValue: string | undefined) => {
    const parameterToAdd: DimensionDetails = {
      dimensionOid: metadata.oid,
      structureCode: "",
      code: metadata.code,
      values: [{ oidPath: [], oid: newValue, desc: newValue, code: null }],
      inherited: false,
    };

    const newParameters = objectParameters.filter((d) => d.dimensionOid !== metadata.oid);
    newParameters.push(parameterToAdd);

    updateParameters(newParameters);
  };

  const updateParameterFromTree = (
    dimensionItem: TgkParameterMetadata,
    newParametersDetails: DimensionDetails[] | undefined
  ) => {
    const parameterToAdd = newParametersDetails?.map((newParameter) => ({
      ...newParameter,
      dimensionOid: dimensionItem.oid,
      structureCode: dimensionItem.dimensionCode,
      code: dimensionItem.code,
    }));

    const newParameters = objectParameters.filter(
      (d) => d.structureCode !== dimensionItem.dimensionCode || d.code !== dimensionItem.code
    );
    newParameters.push(...parameterToAdd);

    updateParameters(newParameters);
  };

  const thisDimensions = objectParameters?.filter((d) => {
    if (d.code !== metadata.code) return false;
    if (d.structureCode != "") return d.structureCode === metadata.dimensionCode;
    return d.dimensionOid === metadata.oid;
  });

  const firstDimension = thisDimensions?.[0];
  const firstValueOfFirstDimension = firstDimension?.values[0];
  const inputLabel = metadata.mandatory ? metadata.desc + " *" : metadata.desc;

  const onFormatDate = (date?: Date): string => {
    return !date
      ? ""
      : (date.getDate() < 10 ? "0" + date.getDate() : date.getDate()) +
          "/" +
          (date.getMonth() < 9 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1) +
          "/" +
          date.getFullYear();
  };

  const resetParameter = (metadata: TgkParameterMetadata) => {
    const newParameters = objectParameters.filter((d) => d.dimensionOid !== metadata.oid);
    updateParameters(newParameters);
  };

  const isInherited = firstDimension?.inherited;

  switch (metadata.type) {
    case "dimension": {
      return (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <TreeDropDownInput
            data-testid="tree-field-parameter-input"
            fetchRoot={async () => {
              const items = await fetchParametersRoot(dbApplication.id, formId, metadata.code);
              return items.map((i) => mapParameterToDimensionItem(i, metadata));
            }}
            fetchChildren={async (item) => {
              const items = await fetchParametersChildren(
                dbApplication.id,
                formId,
                metadata.code,
                item.data.structureCode,
                item.oid
              );
              return items.map((i) => mapParameterToDimensionItem(i, metadata));
            }}
            dimensions={thisDimensions}
            label={inputLabel}
            modalTitle={metadata.desc}
            placeholder={formatMessage("cof.base.select")}
            rootOid={metadata.oid}
            hierarchical={!!metadata.hierarchyCode}
            multiSelectable={metadata.multiSelectable}
            canInherit={inheritableParameters?.filter((d) => d.structureCode === metadata.dimensionCode) ?? []}
            inherited={isInherited}
            onChange={(newParameter) => {
              updateParameterFromTree(metadata, newParameter);
            }}
            disabled={isComponentInstance || isLocked}
            isSearchAvailable={false}
            dbId={dbApplication.id}
            referringScenarios={referringScenarios}
          />
        </div>
      );
    }
    case "text": {
      return (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <TextField
            data-testid="text-field-parameter-input"
            label={inputLabel}
            placeholder={formatMessage("cof.createnewobject.entertext")}
            value={firstValueOfFirstDimension?.desc}
            onChange={(e) => updateParameterFromValue(metadata, (e.target as HTMLInputElement).value)}
            type="text"
            disabled={isComponentInstance || isLocked}
          />
        </div>
      );
    }
    case "number": {
      return (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" style={{ position: "relative" }}>
          <SpinButton
            data-testid="number-field-parameter-input"
            label={inputLabel}
            labelPosition={Position.top}
            defaultValue="0"
            min={0}
            step={1}
            value={firstValueOfFirstDimension?.desc}
            onChange={(_e, newValue) => updateParameterFromValue(metadata, newValue)}
            disabled={isComponentInstance || isLocked}
          />
        </div>
      );
    }
    case "dictionary": {
      return (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <Dropdown
            data-testid="dropdown-field-parameter-input"
            placeholder={formatMessage("cof.base.select")}
            label={inputLabel}
            options={metadata.options.map((o) => ({
              key: o.value,
              text: o.value,
            }))}
            onChange={(_event, option) => updateParameterFromValue(metadata, option.key.toString())}
            selectedKey={firstValueOfFirstDimension?.desc ?? null}
            responsiveMode={ResponsiveMode.large}
            onRenderLabel={(props, defaultRender) =>
              isComponentInstance
                ? defaultRender(props)
                : getLabelRendererWithCloseIcon(() => resetParameter(metadata), props, defaultRender)
            }
            disabled={isComponentInstance || isLocked}
          />
        </div>
      );
    }
    case "date": {
      return (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" style={{ position: "relative" }}>
          <DatePicker
            data-testid="date-field-parameter-input"
            label={inputLabel}
            strings={defaultDatePickerStrings}
            onSelectDate={(e) => {
              const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
              const localISOTime = e ? new Date(e.valueOf() - tzoffset).toISOString() : null;
              updateParameterFromValue(metadata, localISOTime);
            }}
            value={firstValueOfFirstDimension ? new Date(firstValueOfFirstDimension.desc) : undefined}
            formatDate={onFormatDate}
            disabled={isComponentInstance || isLocked}
          />
          {!isComponentInstance && (
            <span
              className="k-icon k-i-close"
              style={{ position: "absolute", top: "4px", right: "7px" }}
              onClick={() => resetParameter(metadata)}
            />
          )}
        </div>
      );
    }
    case "boolean": {
      return (
        <>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <Label style={{ float: "left" }}>{inputLabel}</Label>
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <Checkbox
              checked={firstValueOfFirstDimension?.desc === "true"}
              onChange={(_, checked) => updateParameterFromValue(metadata, checked ? "true" : "false")}
              disabled={isComponentInstance || isLocked}
            />
          </div>
        </>
      );
    }
    default: {
      return (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          UNHANDLED PARAMETER {inputLabel} TYPE {metadata.type}!
        </div>
      );
    }
  }
};
